// The money inputs on the Pénz lens (roadmap 11.P7): quick entry, a transfer between own accounts, the bank's CSV,
// a balance by hand, the payments to review and the latest ones; a payment opens for a correction, a split or a delete.
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { api, onChange, upload } from '../api';
import { day, ft, Label, Panel } from '../ui';

interface Tx { id: number; date: string; time: string | null; amount: number; merchant: string | null; description: string | null; category: string;
  sources: string; account_id: number; currency: string; needs_review: number; transfer_to?: number | null; estimated?: number;
  receipt?: { merchant: string; amount: number; currency: string; invoice_no: string | null; items: string[]; summary: string; subject: string | null } }
interface Account { id: number; name: string; kind: string; currency: string; credit_limit: number; balance: { amount: number; date: string; time: string | null; source: string } | null }
interface List { accounts: Account[]; categories: string[]; review: Tx[]; recent: Tx[]; total: number; sum?: number }
export interface Filter { from?: string; to?: string; category?: string; q?: string; account?: string }

const today = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Budapest' });
const money = (t: Tx) => (t.currency && t.currency !== 'HUF' ? `${t.amount > 0 ? '+' : ''}${t.amount.toLocaleString('hu-HU')} ${t.currency}` : `${t.amount > 0 ? '+' : '−'}${ft(Math.abs(t.amount))}`);
const SRC: Record<string, string> = { sms: 'SMS', applepay: 'Apple Pay', manual: 'kézzel', import: 'CSV', chat: 'chat', receipt: 'nyugta' };
const srcLabel = (s: string) => String(s || '').split(',').map((x) => SRC[x] ?? x).join(' + ');

export function useMoneyList() {
  const [limit, setLimit] = useState(40);
  const [d, setD] = useState<List | null>(null);
  const [filter, setFilter] = useState<Filter>({});
  const qs = Object.entries(filter).filter(([, v]) => v).map(([k, v]) => `&${k}=${encodeURIComponent(String(v))}`).join('');
  const load = useCallback(() => api<List>(`/api/money/txs?limit=${limit}${qs}`).then(setD).catch(() => {}), [limit, qs]);
  useEffect(() => { load(); return onChange(load); }, [load]);
  return { d, load, more: () => setLimit((l) => l + 60), filter, setFilter };
}

/** The quick line on top: "5000 lidl", "+250000 fizetés". Opens the new payment for a correction. */
export function QuickEntry({ focus, onSaved }: { focus?: boolean; onSaved: (t: Tx) => void }) {
  const [text, setText] = useState('');
  const [last, setLast] = useState<{ tx: Tx; merged: boolean; tasksDone: string[] } | null>(null);
  const [err, setErr] = useState('');
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => { if (focus) ref.current?.focus(); }, [focus]);
  const send = async () => {
    if (!text.trim()) return;
    setErr('');
    try { const r = await api<{ tx: Tx; merged: boolean; tasksDone: string[] }>('/api/money/quick', { text }); setLast(r); setText(''); onSaved(r.tx); }
    catch (e: any) { setErr(e.message); }
  };
  return (
    <div className="quick">
      <form className="j-row quick-form" onSubmit={(e) => { e.preventDefault(); send(); }}>
        <input ref={ref} className="j-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="5000 lidl · +250000 fizetés" aria-label="Gyors rögzítés" enterKeyHint="done" />
        <button className="j-btn j-btn--primary" type="submit" aria-label="Rögzítés">+</button>
      </form>
      {err && <p className="err small">{err}</p>}
      {last && <p className="small muted">{last.merged ? 'Ez már megvolt: ' : 'Rögzítve: '}{last.tx.merchant || last.tx.description} {money(last.tx)} · <b>{last.tx.category}</b>
        {last.tasksDone.length > 0 && ' · teendő kipipálva'}</p>}
    </div>
  );
}

function TransferForm({ accounts, onDone }: { accounts: Account[]; onDone: () => void }) {
  const credit = accounts.find((a) => a.kind === 'credit');
  const [f, setF] = useState({ from: 1, to: credit?.id ?? accounts.find((a) => a.id !== 1)?.id ?? 0, amount: '', date: today(), note: '' });
  const set = (k: string, v: any) => setF((x) => ({ ...x, [k]: v }));
  const [err, setErr] = useState('');
  const send = async () => {
    setErr('');
    try { await api('/api/money/transfer', { ...f, amount: Number(f.amount.replace(/\s/g, '')) }); onDone(); } catch (e: any) { setErr(e.message); }
  };
  return (
    <form className="money-form" onSubmit={(e) => { e.preventDefault(); send(); }}>
      <p className="small muted">Saját számlák között (hitelkártya visszatöltése, megtakarítás): nem számít költésnek, mindkét egyenleg mozdul.</p>
      <label>Honnan<select className="j-input" value={f.from} onChange={(e) => set('from', Number(e.target.value))}>{accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></label>
      <label>Hová<select className="j-input" value={f.to} onChange={(e) => set('to', Number(e.target.value))}>{accounts.filter((a) => a.id !== f.from).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></label>
      <label>Összeg (Ft)<input className="j-input" inputMode="numeric" value={f.amount} onChange={(e) => set('amount', e.target.value)} placeholder="256238" /></label>
      <label>Nap<input className="j-input" type="date" value={f.date} onChange={(e) => set('date', e.target.value)} /></label>
      <label>Megjegyzés<input className="j-input" value={f.note} onChange={(e) => set('note', e.target.value)} placeholder="nem kötelező" /></label>
      {err && <p className="err small">{err}</p>}
      <button className="j-btn j-btn--primary" type="submit">Átvezetés rögzítése</button>
    </form>
  );
}

function CsvForm({ accounts, onDone }: { accounts: Account[]; onDone: () => void }) {
  const [acc, setAcc] = useState(1);
  const [res, setRes] = useState<any>(null);
  const [err, setErr] = useState('');
  const send = async (file: File | undefined) => {
    if (!file) return;
    setErr(''); setRes(null);
    try { setRes(await upload(`/api/money/import?account=${acc}`, file)); onDone(); } catch (e: any) { setErr(e.message); }
  };
  return (
    <div className="money-form">
      <p className="small muted">A K&amp;H e-bankból letöltött számlatörténet (CSV). A már ismert tételeket (SMS, Apple Pay) összefésüli, nem duplázza.</p>
      <label>Számla<select className="j-input" value={acc} onChange={(e) => setAcc(Number(e.target.value))}>{accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></label>
      <label className="j-btn file-btn">Fájl kiválasztása<input type="file" accept=".csv,text/csv,text/plain" onChange={(e) => send(e.target.files?.[0])} hidden /></label>
      {err && <p className="err small">{err}</p>}
      {res && <p className="small">{res.account}: {res.rows} sor · <b>{res.added} új</b> · {res.merged} már megvolt{res.skipped ? ` · ${res.skipped} kihagyva` : ''}{res.categorized ? ` · ${res.categorized} kategóriát az AI tippelt` : ''}</p>}
    </div>
  );
}

function BalanceForm({ accounts, onDone }: { accounts: Account[]; onDone: () => void }) {
  const [acc, setAcc] = useState(1);
  const [amount, setAmount] = useState('');
  const [err, setErr] = useState('');
  const cur = accounts.find((a) => a.id === acc);
  const send = async () => {
    setErr('');
    try { await api('/api/money/balance', { account: acc, amount: Number(amount.replace(/\s/g, '')) }); setAmount(''); onDone(); } catch (e: any) { setErr(e.message); }
  };
  return (
    <form className="money-form" onSubmit={(e) => { e.preventDefault(); send(); }}>
      <div className="kv">{accounts.map((a) => <Fragment key={a.id}><span>{a.name}</span><b className="j-num">{a.balance ? `${a.balance.amount.toLocaleString('hu-HU')} ${a.currency === 'HUF' ? 'Ft' : a.currency}` : '–'}</b></Fragment>)}</div>
      <p className="small muted">Ha a bankban mást látsz, írd be, mennyi van most a számlán (a hitelkártyán a tartozás mínusszal).</p>
      <label>Számla<select className="j-input" value={acc} onChange={(e) => setAcc(Number(e.target.value))}>{accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></label>
      <label>Egyenleg most ({cur?.currency === 'HUF' ? 'Ft' : cur?.currency}){<input className="j-input" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={cur?.balance ? String(cur.balance.amount) : ''} />}</label>
      {err && <p className="err small">{err}</p>}
      <button className="j-btn j-btn--primary" type="submit">Beállítás</button>
    </form>
  );
}

/** Átvezetés · CSV · Egyenleg: one open at a time, under the quick line. */
export function MoneyTools({ d, reload }: { d: List; reload: () => void }) {
  const [open, setOpen] = useState<'' | 'transfer' | 'csv' | 'balance'>('');
  const tab = (k: typeof open, label: string) => <button type="button" className={`j-chip ${open === k ? 'on' : ''}`} style={{ '--c': 'var(--mint)' } as any} onClick={() => setOpen(open === k ? '' : k)}>{label}</button>;
  const done = () => { reload(); if (open !== 'csv') setOpen(''); };
  return (
    <div>
      <div className="tabs">{tab('transfer', 'Átvezetés')}{tab('csv', 'CSV')}{tab('balance', 'Egyenleg')}</div>
      {open === 'transfer' && <TransferForm accounts={d.accounts} onDone={done} />}
      {open === 'csv' && <CsvForm accounts={d.accounts} onDone={reload} />}
      {open === 'balance' && <BalanceForm accounts={d.accounts} onDone={done} />}
    </div>
  );
}

function TxEditor({ t, d, onClose }: { t: Tx; d: List; onClose: () => void }) {
  const [f, setF] = useState({ amount: String(t.amount), merchant: t.merchant ?? '', description: t.description ?? '', date: t.date, category: t.category, account_id: t.account_id });
  const [learn, setLearn] = useState(true);
  const [split, setSplit] = useState<{ amount: string; category: string } | null>(null);
  const [sure, setSure] = useState(false);
  const [err, setErr] = useState('');
  const set = (k: string, v: any) => setF((x) => ({ ...x, [k]: v }));
  const run = async (p: Promise<unknown>) => { setErr(''); try { await p; onClose(); } catch (e: any) { setErr(e.message); } };
  const save = () => run(api(`/api/money/tx/${t.id}`, { ...f, amount: Number(String(f.amount).replace(/\s/g, '')), learn: learn && f.category !== t.category }, { method: 'PATCH' }));
  const cats = d.categories;
  return (
    <div className="money-form tx-edit">
      <label>Összeg<input className="j-input" inputMode="numeric" value={f.amount} onChange={(e) => set('amount', e.target.value)} /></label>
      <label>Kinél / mi<input className="j-input" value={f.merchant} onChange={(e) => set('merchant', e.target.value)} /></label>
      <label>Kategória<select className="j-input" value={f.category} onChange={(e) => set('category', e.target.value)}>{cats.map((c) => <option key={c}>{c}</option>)}</select></label>
      {f.category !== t.category && (f.merchant || f.description) && (
        <label className="check"><input type="checkbox" checked={learn} onChange={(e) => setLearn(e.target.checked)} /> Jegyezze meg: „{f.merchant || f.description}” mindig {f.category}</label>
      )}
      <label>Nap<input className="j-input" type="date" value={f.date} onChange={(e) => set('date', e.target.value)} /></label>
      <label>Számla<select className="j-input" value={f.account_id} onChange={(e) => set('account_id', Number(e.target.value))}>{d.accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select></label>
      <label>Megjegyzés<input className="j-input" value={f.description} onChange={(e) => set('description', e.target.value)} /></label>
      <p className="small muted">Forrás: {srcLabel(t.sources)}{t.estimated ? ' · becsült forintösszeg, a banki sor pontosítja' : ''}</p>
      {t.receipt && (
        <div className="receipt small">
          <b>Nyugta:</b> {t.receipt.summary}{t.receipt.currency !== 'HUF' ? ` · ${t.receipt.amount.toLocaleString('hu-HU')} ${t.receipt.currency}` : ''}{t.receipt.invoice_no ? ` · ${t.receipt.invoice_no}` : ''}
          {t.receipt.items?.length > 0 && <ul>{t.receipt.items.map((x, i) => <li key={i}>{x}</li>)}</ul>}
          {t.receipt.subject && <div className="muted">levél: {t.receipt.subject}</div>}
        </div>
      )}
      {err && <p className="err small">{err}</p>}
      <div className="j-row">
        <button type="button" className="j-btn j-btn--primary" onClick={save}>Mentés</button>
        <button type="button" className="j-btn" onClick={() => setSplit(split ? null : { amount: '', category: cats.includes('Kiadott lakás') ? 'Kiadott lakás' : cats[0]! })}>Felosztás</button>
        <button type="button" className={`j-btn ${sure ? 'danger' : ''}`} onClick={() => (sure ? run(api(`/api/money/tx/${t.id}`, undefined, { method: 'DELETE' })) : setSure(true))}>{sure ? 'Biztosan törlöd?' : 'Törlés'}</button>
        <button type="button" className="j-btn linkish" onClick={onClose}>Mégse</button>
      </div>
      {split && (
        <div className="split">
          <p className="small muted">A tétel egy része másik kategóriába megy (pl. a bérlő rezsirésze). A maradék marad {t.category}.</p>
          <div className="j-row">
            <input className="j-input" inputMode="numeric" placeholder="rész összege" value={split.amount} onChange={(e) => setSplit({ ...split, amount: e.target.value })} />
            <select className="j-input" value={split.category} onChange={(e) => setSplit({ ...split, category: e.target.value })}>{cats.map((c) => <option key={c}>{c}</option>)}</select>
            <button type="button" className="j-btn" onClick={() => run(api(`/api/money/tx/${t.id}/split`, { amount: Number(split.amount.replace(/\s/g, '')), category: split.category }))}>Felosztom</button>
          </div>
        </div>
      )}
    </div>
  );
}

function TxRow({ t, d, k, openId, setOpen }: { t: Tx; d: List; k: string; openId: string | null; setOpen: (k: string | null) => void }) {
  const open = openId === k;
  const acc = d.accounts.find((a) => a.id === t.account_id);
  return (
    <div className={`tx ${open ? 'open' : ''}`}>
      <button type="button" className="tx-row" onClick={() => setOpen(open ? null : k)} aria-expanded={open}>
        <span className="t">{day(t.date)}</span>
        <span className="who">{t.merchant || t.description || '–'}<em>{t.category}{t.receipt ? ' · nyugta' : ''}{acc && acc.id !== 1 ? ` · ${acc.name}` : ''}{t.needs_review ? ' · ?' : ''}</em></span>
        <span className={`j-num amt ${t.amount > 0 ? 'in' : ''}`}>{money(t)}</span>
      </button>
      {open && <TxEditor t={t} d={d} onClose={() => setOpen(null)} />}
    </div>
  );
}

export function TxLists({ d, more, openId, setOpen, filter, setFilter, reload }: { d: List; more: () => void; openId: string | null; setOpen: (k: string | null) => void;
  filter?: Filter; setFilter?: (f: Filter) => void; reload?: () => void }) {
  const [showF, setShowF] = useState(false);
  const f = filter ?? {};
  const set = (k: keyof Filter, v: string) => setFilter?.({ ...f, [k]: v || undefined });
  const allOk = () => api('/api/money/review/ok', {}).then(() => reload?.()).catch(() => {});
  const active = Object.values(f).some(Boolean);
  return (
    <>
      {d.review.length > 0 && (
        <Panel c="amber"><Label c="amber">Átnézendő · {d.review.length}</Label>
          <p className="small muted">Nem biztos a kategória vagy a kereskedő. Koppints rá, és mentsd (a jó kategóriát megjegyzi), vagy ha mind jó, egy gombbal.</p>
          <button type="button" className="j-btn" onClick={allOk}>Mind jó</button>
          {d.review.map((t) => <TxRow key={`r${t.id}`} k={`r${t.id}`} t={t} d={d} openId={openId} setOpen={setOpen} />)}
        </Panel>
      )}
      <Panel c="mint"><Label c="mint">Tételek · {d.total}{active && d.sum != null ? ` · összesen ${money({ amount: d.sum } as Tx)}` : ''}</Label>
        {setFilter && (showF || active ? (
          <div className="money-form">
            <div className="j-row">
              <input className="j-input" value={f.q ?? ''} onChange={(e) => set('q', e.target.value)} placeholder="keresés (bolt, leírás)" />
              <select className="j-input" value={f.category ?? ''} onChange={(e) => set('category', e.target.value)}><option value="">minden kategória</option>{d.categories.map((c) => <option key={c}>{c}</option>)}</select>
            </div>
            <div className="j-row">
              <label>ettől<input className="j-input" type="date" value={f.from ?? ''} onChange={(e) => set('from', e.target.value)} /></label>
              <label>eddig<input className="j-input" type="date" value={f.to ?? ''} onChange={(e) => set('to', e.target.value)} /></label>
              <select className="j-input" value={f.account ?? ''} onChange={(e) => set('account', e.target.value)}><option value="">minden számla</option>{d.accounts.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}</select>
            </div>
            <button type="button" className="j-btn linkish" onClick={() => { setFilter({}); setShowF(false); }}>Szűrő törlése</button>
          </div>
        ) : <button type="button" className="j-btn linkish" onClick={() => setShowF(true)}>Szűrés…</button>)}
        {d.recent.map((t) => <TxRow key={t.id} k={String(t.id)} t={t} d={d} openId={openId} setOpen={setOpen} />)}
        {d.recent.length < d.total && <button type="button" className="j-btn linkish more" onClick={more}>Többet</button>}
      </Panel>
    </>
  );
}
